// components/ProjectExpenditures.jsx
import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Papa from 'papaparse'; // Import papaparse for CSV generation

import Table from '../../shared/components/layout/Table';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Search from '../../shared/components/ui/Search';
import FileDownload from '../../../modules/shared/assets/svg/download.svg';
import NoDataFound from '../components/layout/NoDataFound';
import PaginationComponent from '../../shared/components/PaginationComponent';
import { transformData } from '../../super-admin/utils/transformData';
import { getProjectExpendituresReports } from '../services/api/implementation/impl';
import Button from '../../shared/components/ui/Button';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const ProjectExpenditures = () => {
  const userId = Cookies.get('userId');
  /*
   * -------------------------------------------------
   * Component State
   * -------------------------------------------------
   */
  const { collapsed } = useSidebar();
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // Items per page
  const [isDownloading, setIsDownloading] = useState(false); // New state for download status
  const [dotCount, setDotCount] = useState(0); // State for dots animation

  /*
   * -------------------------------------------------
   * Fetching Project Expenditures from the API (Server-Side Pagination)
   * -------------------------------------------------
   */
  const {
    data: expenditureResponse,
    isFetching: isFetchingExpenditures,
    isError,
    error
  } = useQuery({
    queryKey: ['projectExpenditures', page, limit],
    queryFn: () => getProjectExpendituresReports(page, limit),
    keepPreviousData: true,
    refetchOnMount: 'always'
  });

  // If your API returns { data: [...], meta: {...} }
  const allExpenditures = expenditureResponse?.data || [];
  const totalItems = expenditureResponse?.meta?.total || 0;
  const totalPages = Math.ceil(totalItems / limit);

  /*
   * -------------------------------------------------
   * Transform the Data for Table
   * -------------------------------------------------
   */
  const expendituresDataFormat = ['id', 'BlockName', 'ProjectName', 'NoOfBills', 'TotalExpense'];

  const processedExpenditures = allExpenditures.map((expenditure) => ({
    id: expenditure.id || 'no-id',
    BlockName: expenditure.blockName || 'N/A',
    ProjectName: expenditure.projectName || 'N/A',
    NoOfBills: expenditure.numberOfBills || 0,
    TotalExpense: expenditure.totalExpense !== undefined ? `₹ ${expenditure.totalExpense}` : '₹ 0'
  }));

  const expendituresData = processedExpenditures.map((row) =>
    transformData(row, expendituresDataFormat)
  );

  /*
   * -------------------------------------------------
   * Handlers
   * -------------------------------------------------
   */
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  /**
   * Handle Download CSV using papaparse
   */
  const handleDownload = async () => {
    if (isDownloading) return; // Prevent multiple clicks

    setIsDownloading(true);
    try {
      // Fetch all data with page=1 and limit=1000
      const response = await getProjectExpendituresReports(1, 1000);
      const expenditures = response?.data || [];

      if (expenditures.length === 0) {
        alert('No data available to download.');
        return;
      }

      // Define CSV headers
      const headers = ['Block Name', 'Project Name', 'Number of Bills', 'Total Expense'];

      // Map data to CSV rows as objects without the currency symbol
      const rows = expenditures.map((expenditure) => ({
        'Block Name': expenditure.blockName || 'N/A',
        'Project Name': expenditure.projectName || 'N/A',
        'Number of Bills': expenditure.numberOfBills || 0,
        'Total Expense': expenditure.totalExpense !== undefined ? expenditure.totalExpense : 0
      }));

      // Convert to CSV using papaparse
      const csv = Papa.unparse({
        fields: headers,
        data: rows
      });

      // Create a Blob from the CSV content
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

      // Create a temporary link to trigger the download
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', `project_expenditures.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Download failed:', downloadError);
      alert('Failed to download the file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  /*
   * -------------------------------------------------
   * Dots Animation Effect
   * -------------------------------------------------
   */

  // Track page view
  useEffect(() => {
    mixpanel.track('Project Expenditures Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    let interval;
    if (isDownloading) {
      interval = setInterval(() => {
        setDotCount((prev) => (prev < 3 ? prev + 1 : 0));
      }, 500);
    } else {
      setDotCount(0);
    }

    return () => clearInterval(interval);
  }, [isDownloading]);

  const renderDownloadingText = () => {
    return `Downloading${'.'.repeat(dotCount)}`;
  };

  /*
   * -------------------------------------------------
   * Render
   * -------------------------------------------------
   */
  return (
    <div className="w-full flex flex-col relative z-5 bg-gray-100 pb-10 overflow-hidden">
      {/* Top Bar: Search, Download */}
      <div
        className={`transition-all duration-300 ease-in-out  flex items-center justify-between px-4 py-4 bg-white fixed top-[9.5rem] border-t-2 border-stone-100 ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'} shadow-sm`}>
        {/* Uncomment the Search component if needed */}
        {/* <Search placeholder="Search here..." /> */}
        <div></div>
        <div className="flex items-center gap-3 px-4">
          <button
            className={`relative flex items-center justify-center ${
              isDownloading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            }`}
            onClick={handleDownload}
            disabled={isDownloading}
            aria-label="Download CSV">
            <img height={35} width={35} src={FileDownload} alt="File Download Icon" />
            {isDownloading && (
              <span className="absolute top-10 left-0 text-sm text-gray-700">
                {renderDownloadingText()}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Expenditures Table */}
      <div className="mt-24 w-full h-full">
        {isFetchingExpenditures ? (
          <div className="pt-12 text-center">
            <p>Loading...</p>
          </div>
        ) : isError ? (
          <div className="pt-12 text-center text-red-500">
            <p>Error: {error.message}</p>
          </div>
        ) : expendituresData && expendituresData.length > 0 ? (
          <GridWrapper width={100}>
            <div className="mt-[10rem] h-[28rem] relative">
              <Table
                headers={['Block Name', 'Project Name', 'No. of Bills', 'Total Expense']}
                data={expendituresData}
                identifier="id"
                isViewEnable={false}
                isDeleteEnable={false}
                isEditEnable={false}
                isWrap={true}
              />

              <PaginationComponent
                page={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isFetching={isFetchingExpenditures}
              />
            </div>
          </GridWrapper>
        ) : (
          <div className="pt-12">
            <NoDataFound disabledBtn screenHeight={true} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectExpenditures;
